import { createRandomId } from "RandomId";
import {
  AGENDA_RESOURCE_TYPES,
  isValidYouTubeResourceUrl,
  normalizeAgendaImageResourceHeading,
  normalizeResourceUrl,
} from "./agendaResources";
import {
  isSavableIframeWebsiteLink,
  normalizeIframeWebsiteLink,
  toBrowserWebsiteLink,
} from "./iframeWebsiteLink";

export const QUICK_LAUNCH_TYPES = {
  PDF: "pdf",
  AUDIO: "audio",
  IMAGE: "image",
  LINK: "link",
  YOUTUBE: "youtube",
  NOTE: "note",
  CONTENT_BOX: "contentBox",
};

export const QUICK_LAUNCH_OPEN_TARGETS = {
  APP: "app",
  NEW_TAB: "newTab",
};

export const QUICK_LAUNCH_RESOURCE_SECTIONS = [
  { type: QUICK_LAUNCH_TYPES.PDF, heading: "PDFs" },
  { type: QUICK_LAUNCH_TYPES.AUDIO, heading: "Audio Files" },
  { type: QUICK_LAUNCH_TYPES.LINK, heading: "Links" },
  { type: QUICK_LAUNCH_TYPES.CONTENT_BOX, heading: "Content Boxes", comingSoon: true },
];

const TYPE_VALUES = Object.values(QUICK_LAUNCH_TYPES);

export const normalizeQuickLaunchOpenTarget = (value) => (
  value === QUICK_LAUNCH_OPEN_TARGETS.NEW_TAB
    ? QUICK_LAUNCH_OPEN_TARGETS.NEW_TAB
    : QUICK_LAUNCH_OPEN_TARGETS.APP
);

export const shouldOpenQuickLaunchResourceInNewTab = (resource = {}) => (
  resource?.type
  && resource.type !== QUICK_LAUNCH_TYPES.CONTENT_BOX
  && resource.type !== QUICK_LAUNCH_TYPES.IMAGE
  && normalizeQuickLaunchOpenTarget(resource.openTarget) === QUICK_LAUNCH_OPEN_TARGETS.NEW_TAB
);

export const getQuickLaunchTypeLabel = (type) => {
  switch (type) {
    case QUICK_LAUNCH_TYPES.PDF:
      return "PDF";
    case QUICK_LAUNCH_TYPES.AUDIO:
      return "Audio";
    case QUICK_LAUNCH_TYPES.IMAGE:
      return "Image";
    case QUICK_LAUNCH_TYPES.LINK:
      return "Link";
    case QUICK_LAUNCH_TYPES.YOUTUBE:
      return "YouTube";
    case QUICK_LAUNCH_TYPES.NOTE:
      return "Note";
    case QUICK_LAUNCH_TYPES.CONTENT_BOX:
      return "Content Box";
    default:
      return "Resource";
  }
};

export const getQuickLaunchIconClass = (type) => {
  switch (type) {
    case QUICK_LAUNCH_TYPES.PDF:
      return "fa fa-file-pdf-o";
    case QUICK_LAUNCH_TYPES.AUDIO:
      return "fa fa-volume-up";
    case QUICK_LAUNCH_TYPES.IMAGE:
      return "fa fa-picture-o";
    case QUICK_LAUNCH_TYPES.LINK:
      return "fa fa-link";
    case QUICK_LAUNCH_TYPES.YOUTUBE:
      return "fa fa-youtube-play";
    case QUICK_LAUNCH_TYPES.NOTE:
      return "fa fa-sticky-note";
    case QUICK_LAUNCH_TYPES.CONTENT_BOX:
      return "fa fa-th-large";
    default:
      return "fa fa-file-o";
  }
};

const getContentBoxExternalUrl = (box = {}) => {
  if (!box || typeof box !== "object") return "";

  if (box.canva) {
    return box.canvaEditUrl || box.canvaViewUrl || box.content || "";
  }

  if (box.slideshow || box.googleDocSheet) {
    return box.googleEditUrl || box.webViewLink || box.shareUrl || box.content || "";
  }

  if (box.pdf || box.powerPoint || box.iFrame || box.imageBox) {
    const contentUrl = box.content || "";
    return box.iFrame ? (toBrowserWebsiteLink(contentUrl) || contentUrl) : contentUrl;
  }

  return "";
};

export const getQuickLaunchExternalOpenUrl = (resource = {}, resolvedUrl = "") => {
  if (!resource || typeof resource !== "object") return "";

  if (resource.type === QUICK_LAUNCH_TYPES.CONTENT_BOX) {
    return getContentBoxExternalUrl(resource.box);
  }

  const url = resolvedUrl || resource.url || "";
  if (
    resource.type === QUICK_LAUNCH_TYPES.LINK
    || resource.type === QUICK_LAUNCH_TYPES.YOUTUBE
  ) {
    return toBrowserWebsiteLink(url) || url;
  }

  return url;
};

const safeDecodeURIComponent = (value = "") => {
  const text = String(value);
  try {
    return decodeURIComponent(text);
  } catch (_) {
    return text;
  }
};

const getUrlFilename = (url = "") => {
  try {
    const parsedUrl = new URL(url);
    const name = parsedUrl.pathname.split("/").filter(Boolean).pop();
    return name ? safeDecodeURIComponent(name) : "";
  } catch (_) {
    return "";
  }
};

export const stripFileExtension = (filename = "") => {
  const base = String(filename).trim();
  const lastDot = base.lastIndexOf(".");
  if (lastDot <= 0) return base;
  return base.slice(0, lastDot);
};

export const QUICK_LAUNCH_CARD_TITLE_MAX_LENGTH = 18;

export const truncateQuickLaunchCardTitle = (
  title = "",
  maxLength = QUICK_LAUNCH_CARD_TITLE_MAX_LENGTH
) => {
  const text = String(title).trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

/**
 * Human-friendly title from a Firebase download URL, storage path, or filename.
 * e.g. users/uid/Beginner Scales.pdf -> "Beginner Scales"
 */
export const getQuickLaunchDisplayTitleFromUrl = (urlOrPath = "", type = QUICK_LAUNCH_TYPES.PDF) => {
  const raw = String(urlOrPath).trim();
  if (!raw) return getQuickLaunchTypeLabel(type);

  let pathLike = raw;
  try {
    const parsed = new URL(raw);
    if (parsed.hostname.includes("firebasestorage.googleapis.com")) {
      const encoded = parsed.pathname.split("/o/")[1] || "";
      pathLike = safeDecodeURIComponent(encoded.split("?")[0]);
    } else {
      pathLike = safeDecodeURIComponent(parsed.pathname);
    }
  } catch (_) {
    pathLike = raw;
  }

  const lastSegment = pathLike.split("/").filter(Boolean).pop() || pathLike;
  const withoutExtension = stripFileExtension(safeDecodeURIComponent(lastSegment));
  return withoutExtension || getQuickLaunchTypeLabel(type);
};

const resolveQuickLaunchTitle = (resource = {}, type, url) => {
  const explicitTitle = String(resource.title || resource.name || "").trim();
  const looksLikeStoragePath =
    explicitTitle.includes("/") || /\.(pdf|mp3|wav|ogg|m4a|aac|jpe?g|png|gif|webp|bmp)$/i.test(explicitTitle);

  if (
    type !== QUICK_LAUNCH_TYPES.LINK
    && type !== QUICK_LAUNCH_TYPES.YOUTUBE
    && url
    && (!explicitTitle || looksLikeStoragePath)
  ) {
    return getQuickLaunchDisplayTitleFromUrl(url, type);
  }

  if (explicitTitle) return explicitTitle;
  if (type === QUICK_LAUNCH_TYPES.LINK || type === QUICK_LAUNCH_TYPES.YOUTUBE) {
    return getUrlFilename(url) || getQuickLaunchTypeLabel(type);
  }
  return getQuickLaunchDisplayTitleFromUrl(url, type);
};

export const normalizeQuickLaunchResource = (resource = {}, index = 0) => {
  const type = TYPE_VALUES.includes(resource.type) ? resource.type : QUICK_LAUNCH_TYPES.LINK;
  const id = String(resource.id || createRandomId());
  const rawUrl = String(resource.url || "").trim();

  if (type === QUICK_LAUNCH_TYPES.CONTENT_BOX) {
    const contentBoxId = String(resource.contentBoxId || "").trim();
    if (!contentBoxId) return null;
    return {
      id,
      type,
      title: String(resource.title || resource.name || "Content Box"),
      contentBoxId,
      classId: resource.classId ? String(resource.classId) : "",
      ...(resource.box ? { box: resource.box } : {}),
      order: typeof resource.order === "number" ? resource.order : index,
    };
  }

  if (type === QUICK_LAUNCH_TYPES.NOTE) {
    return {
      id,
      type,
      title: String(resource.title || resource.name || "Note"),
      text: String(resource.text || ""),
      fontSize: Number.isFinite(Number(resource.fontSize)) ? Number(resource.fontSize) : 24,
      fontColor: String(resource.fontColor || "#111827"),
      backgroundColor: String(resource.backgroundColor || "#fff7cc"),
      order: typeof resource.order === "number" ? resource.order : index,
    };
  }

  let url = rawUrl;
  if (type === QUICK_LAUNCH_TYPES.LINK) {
    url = normalizeIframeWebsiteLink(rawUrl);
    if (!url || !isSavableIframeWebsiteLink(url, { allowInternal: true })) return null;
  } else if (type === QUICK_LAUNCH_TYPES.YOUTUBE) {
    url = normalizeResourceUrl(rawUrl);
    if (!url || !isValidYouTubeResourceUrl(rawUrl)) return null;
  } else if (!rawUrl) {
    return null;
  }

  const title = resolveQuickLaunchTitle(resource, type, url);

  const normalized = {
    id,
    type,
    title: String(title),
    url,
    order: typeof resource.order === "number" ? resource.order : index,
  };

  if (type === QUICK_LAUNCH_TYPES.IMAGE) {
    const heading = normalizeAgendaImageResourceHeading(resource.heading);
    if (heading) {
      normalized.heading = heading;
    }
  }

  if (type === QUICK_LAUNCH_TYPES.LINK) {
    normalized.openTarget = normalizeQuickLaunchOpenTarget(resource.openTarget);
  } else if (
    type !== QUICK_LAUNCH_TYPES.CONTENT_BOX
    && resource.openTarget !== undefined
  ) {
    normalized.openTarget = normalizeQuickLaunchOpenTarget(resource.openTarget);
  }

  return normalized;
};

export const sortQuickLaunchResources = (resources = []) =>
  [...resources].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.title.localeCompare(b.title);
  });

export const getQuickLaunchResourceSections = ({ includeComingSoon = false } = {}) =>
  includeComingSoon
    ? QUICK_LAUNCH_RESOURCE_SECTIONS
    : QUICK_LAUNCH_RESOURCE_SECTIONS.filter((section) => !section.comingSoon);

export const groupQuickLaunchResourcesBySection = (resources = [], options = {}) => {
  const sectionDefs = getQuickLaunchResourceSections(options);
  const buckets = Object.fromEntries(sectionDefs.map((section) => [section.type, []]));

  resources.forEach((resource) => {
    if (buckets[resource.type]) {
      buckets[resource.type].push(resource);
    }
  });

  return sectionDefs.map((section) => ({
    ...section,
    resources: buckets[section.type] || [],
  }));
};

export const getQuickLaunchResources = (classData = {}) => {
  const raw = Array.isArray(classData.quickLaunchResources) ? classData.quickLaunchResources : [];
  return sortQuickLaunchResources(
    raw.map((item, index) => normalizeQuickLaunchResource(item, index)).filter(Boolean)
  );
};

export const createQuickLaunchResource = ({ type, title, url, order, openTarget } = {}) =>
  normalizeQuickLaunchResource({
    id: createRandomId(),
    type,
    title,
    url: type === QUICK_LAUNCH_TYPES.LINK ? normalizeIframeWebsiteLink(url) : url,
    ...(type === QUICK_LAUNCH_TYPES.LINK ? { openTarget } : {}),
    order: typeof order === "number" ? order : 0,
  });

/** Map an agenda step resource to a Quick Launch viewer payload (PDF / audio / YouTube / content box). */
export const agendaResourceToQuickLaunchResource = (resource = {}) => {
  const { type, url, id, title } = resource;

  if (type === AGENDA_RESOURCE_TYPES.CONTENT_BOX) {
    return normalizeQuickLaunchResource({
      id,
      type: QUICK_LAUNCH_TYPES.CONTENT_BOX,
      title,
      contentBoxId: resource.contentBoxId,
      classId: resource.classId,
      ...(resource.box ? { box: resource.box } : {}),
    });
  }

  if (type === AGENDA_RESOURCE_TYPES.NOTE) {
    return normalizeQuickLaunchResource({
      id,
      type: QUICK_LAUNCH_TYPES.NOTE,
      title: "Note",
      text: resource.text,
      fontSize: resource.fontSize,
      fontColor: resource.fontColor,
      backgroundColor: resource.backgroundColor,
    });
  }

  if (!url) return null;

  if (type === AGENDA_RESOURCE_TYPES.FILE && resource.fileType === "pdf") {
    return normalizeQuickLaunchResource({
      id,
      type: QUICK_LAUNCH_TYPES.PDF,
      title,
      url,
      openTarget: resource.openTarget,
    });
  }

  if (type === AGENDA_RESOURCE_TYPES.AUDIO) {
    return normalizeQuickLaunchResource({
      id,
      type: QUICK_LAUNCH_TYPES.AUDIO,
      title,
      url,
      openTarget: resource.openTarget,
    });
  }

  if (type === AGENDA_RESOURCE_TYPES.IMAGE) {
    return normalizeQuickLaunchResource({
      id,
      type: QUICK_LAUNCH_TYPES.IMAGE,
      title,
      url,
      heading: resource.heading,
    });
  }

  if (type === AGENDA_RESOURCE_TYPES.YOUTUBE) {
    return normalizeQuickLaunchResource({
      id,
      type: QUICK_LAUNCH_TYPES.YOUTUBE,
      title,
      url,
      openTarget: resource.openTarget,
    });
  }

  if (type === AGENDA_RESOURCE_TYPES.LINK) {
    return normalizeQuickLaunchResource({
      id,
      type: QUICK_LAUNCH_TYPES.LINK,
      title,
      url,
      openTarget: resource.openTarget,
    });
  }

  return null;
};

export const reorderQuickLaunchResources = (resources = [], sourceIndex, destinationIndex) => {
  const next = [...resources];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, moved);
  return next.map((item, index) => ({ ...item, order: index }));
};

/** Reorder resources within a single type lane and rebuild global order indices. */
export const reorderQuickLaunchResourcesInSection = (
  resources = [],
  sectionType,
  sourceIndex,
  destinationIndex
) => {
  if (sourceIndex === destinationIndex) return resources;

  const grouped = groupQuickLaunchResourcesBySection(resources);
  const sectionIndex = grouped.findIndex((section) => section.type === sectionType);
  if (sectionIndex === -1) return resources;

  const sectionResources = [...grouped[sectionIndex].resources];
  const [moved] = sectionResources.splice(sourceIndex, 1);
  sectionResources.splice(destinationIndex, 0, moved);

  const nextGrouped = grouped.map((section, index) =>
    index === sectionIndex ? { ...section, resources: sectionResources } : section
  );

  const merged = nextGrouped.flatMap((section) => section.resources);
  const knownIds = new Set(merged.map((item) => item.id));
  resources.forEach((resource) => {
    if (!knownIds.has(resource.id)) {
      merged.push(resource);
    }
  });

  return merged.map((item, index) => ({ ...item, order: index }));
};

export const mergeResourceIntoClassList = (existing = [], resource) => {
  const normalized = normalizeQuickLaunchResource(resource);
  if (!normalized) return existing;

  const hasDuplicate = existing.some(
    (item) => item.type === normalized.type && item.url === normalized.url
  );
  if (hasDuplicate) return existing;

  return sortQuickLaunchResources([
    ...existing,
    { ...normalized, order: existing.length },
  ]);
};

export const copyResourceToClassesPayload = (resource, existingResources = []) =>
  mergeResourceIntoClassList(existingResources, resource);
